import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    CheckIcon,
    ClipboardDocumentIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    GlobeAltIcon,
    ServerIcon,
    UserIcon,
} from "@heroicons/react/24/outline";

import { getAuditLogDetails } from "../api/auditApi";
import {
    AUDIT_STATUS_CLASSES,
    formatAdminIdentity,
    formatAuditDate,
    formatAuditLabel,
    formatAuditRole,
} from "../audit-utils";

// ============================================================================
// Helpers
// ============================================================================

const httpMethodColors: Record<string, string> = {
    GET: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    POST: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    PUT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    PATCH: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    DELETE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

// ============================================================================
// Component
// ============================================================================

export const AuditLogDetailsRoute = () => {
    const { logId } = useParams<{ logId: string }>();
    const [correlationCopied, setCorrelationCopied] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ["audit-log-details", logId],
        queryFn: () => getAuditLogDetails(logId!),
        enabled: !!logId,
    });

    const log = data?.data;

    const copyCorrelationId = async () => {
        if (!log?.correlationId || !navigator.clipboard) return;

        await navigator.clipboard.writeText(log.correlationId);
        setCorrelationCopied(true);
        window.setTimeout(() => setCorrelationCopied(false), 2000);
    };

    // ────────────────────────────────────────────────────────────────────────
    // Loading / Error
    // ────────────────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !log) {
        return (
            <div className="space-y-4">
                <Link
                    to="/audit-logs/logs"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" /> Back to Logs
                </Link>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        Failed to load audit log details
                    </p>
                </div>
            </div>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // Try to parse JSON strings for display
    // ────────────────────────────────────────────────────────────────────────

    const tryPrettyJson = (raw: string | null) => {
        if (!raw) return null;
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw;
        }
    };

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Back */}
            <Link
                to="/audit-logs/logs"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Logs
            </Link>

            {/* Header */}
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                        {formatAuditLabel(log.action)}
                    </h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${AUDIT_STATUS_CLASSES[log.status] ?? AUDIT_STATUS_CLASSES.SUCCESS
                                }`}
                        >
                            {formatAuditLabel(log.status)}
                        </span>
                        <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${httpMethodColors[log.httpMethod ?? "GET"] ?? httpMethodColors.GET
                                }`}
                        >
                            {log.httpMethod ?? "Unknown method"}
                        </span>
                        <span className="text-xs text-slate-400">{formatAuditLabel(log.actionCategory)}</span>
                    </div>
                </div>
            </div>

            {/* Change Summary */}
            {log.changeSummary && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Summary
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{log.changeSummary}</p>
                </div>
            )}

            {/* Error message */}
            {log.errorMessage && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
                    <span className="font-medium">Error:</span> {log.errorMessage}
                </div>
            )}

            {/* Grid of Info Cards */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Admin Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <UserIcon className="h-4 w-4 text-blue-500" /> Admin
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Email", formatAdminIdentity(log.adminEmail, log.adminId)],
                            ["Role", formatAuditRole(log.adminRole)],
                            ["Admin ID", log.adminId],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Target / Resource Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ServerIcon className="h-4 w-4 text-amber-500" /> Resource
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Resource Type", formatAuditLabel(log.resourceType)],
                            ["Resource ID", log.resourceId],
                            ["Target User", log.targetUserEmail ?? log.targetUserId ?? "—"],
                            ["Target User Type", formatAuditLabel(log.targetUserType)],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Request Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <GlobeAltIcon className="h-4 w-4 text-emerald-500" /> Request
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Endpoint", log.endpoint],
                            ["Method", log.httpMethod],
                            ["Response Status", log.responseStatus != null ? String(log.responseStatus) : "—"],
                            ["Duration", log.durationMs != null ? `${log.durationMs}ms` : "—"],
                            ["IP Address", log.ipAddress ?? "—"],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Session Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ClockIcon className="h-4 w-4 text-purple-500" /> Session
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Performed At", formatAuditDate(log.performedAt, "long")],
                            ["Session ID", log.sessionId ?? "—"],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                        <div className="flex items-center justify-between gap-3">
                            <dt className="text-slate-500 dark:text-slate-400">Correlation ID</dt>
                            <dd className="flex min-w-0 items-center gap-2">
                                <span className="max-w-[180px] truncate font-mono text-xs font-medium text-slate-900 dark:text-white">
                                    {log.correlationId ?? "—"}
                                </span>
                                {log.correlationId ? (
                                    <button
                                        type="button"
                                        onClick={copyCorrelationId}
                                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 transition hover:border-primary-300 hover:text-primary-600 dark:border-slate-700 dark:text-slate-300 dark:hover:border-primary-700 dark:hover:text-primary-400"
                                    >
                                        {correlationCopied ? (
                                            <CheckIcon className="h-3.5 w-3.5" />
                                        ) : (
                                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                        )}
                                        {correlationCopied ? "Copied" : "Copy"}
                                    </button>
                                ) : null}
                            </dd>
                        </div>
                        <div>
                            <dt className="mb-1 text-slate-500 dark:text-slate-400">User Agent</dt>
                            <dd className="break-all text-xs text-slate-600 dark:text-slate-300">
                                {log.userAgent || "—"}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Old / New Values */}
            {(log.oldValue || log.newValue) && (
                <div className="grid gap-6 lg:grid-cols-2">
                    {log.oldValue && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-2 text-sm font-semibold text-rose-600 dark:text-rose-400">
                                Old Value
                            </h3>
                            <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {tryPrettyJson(log.oldValue)}
                            </pre>
                        </div>
                    )}
                    {log.newValue && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                New Value
                            </h3>
                            <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {tryPrettyJson(log.newValue)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Request Body */}
            {log.requestBody && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Request Body
                    </h3>
                    <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {tryPrettyJson(log.requestBody)}
                    </pre>
                </div>
            )}
        </div>
    );
};
